import React, { useContext, useState } from 'react';

import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { historyShape, customerShape, passengersShape } from 'constants/propTypes';

import moment from 'moment';
import Spinner from 'admin-components/components/Spinner';
import ValidationForm from 'components/ui/ValidationForm';
import { CurrencyInput } from 'components/ui/Inputs';

import useCustomerPresentMutation from 'components/mutations/useCustomerPresentMutation';
import { Errors } from 'components/ui/Payments/SplitPayment/SplitPayment.styles';
import SplitPayment from 'components/ui/Payments/SplitPayment';

import Modal from 'components/ui/Modal/Modal';
import { ADD_ADDITIONAL_DETAILS } from 'store/mutations';
import { useMutation } from '@apollo/client';
import { MOBILE_TERMINAL_ID_STORAGE_KEY } from 'constants/user';
import { AlertContext } from '../../AlertProvider';

const CustomerPresentCardPayment = ({
  customer,
  reference,
  isOnHold,
  currency,
  history,
  currentAmount,
  onAmountChange,
  totalPriceToBePaid,
  passengers,
  isExchange,
  user,
  isPutOnHoldWithDeposit
}) => {
  const isFlowForwardCapped = new URLSearchParams(history.location.search).get('isFlowForwardCapped') === 'true';

  const [showSPTMError, setShowSPTMError] = useState(false);
  const [, setAlert] = useContext(AlertContext);
  const [processCardPayment, { loading: loadingPayment }] = useCustomerPresentMutation({
    customer,
    reference,
    currency,
    history,
    setAlert,
    passengers,
    isExchange,
    isFlowForwardCapped
  });

  const [addAdditionalDetails, { loading: addAdditionalDetailsLoading }] = useMutation(ADD_ADDITIONAL_DETAILS);

  const loading = loadingPayment || addAdditionalDetailsLoading;

  if (loading) {
    return <Spinner />;
  }

  const toggleSPTMErrorModal = () => setShowSPTMError(!showSPTMError);

  return (
    <ValidationForm
      validators={{
        amount: (values) => Number(values.amount) > 0 && Number(values.amount) <= totalPriceToBePaid
      }}
      fields={{ amount: currentAmount }}
      overrideFields={{ amount: currentAmount }}
      validateInitialFields
    >
      {({ fields, onChange, hasInvalidInputs, invalidInputs }) => {
        const handleChange = (e) => {
          onChange(e);

          if (onAmountChange) {
            onAmountChange(e.target.value);
          }
        };

        const handleSubmit = async (e) => {
          e.preventDefault();

          try {
            await processCardPayment(fields.amount, totalPriceToBePaid);

            if (user && user.isStation && localStorage.getItem(MOBILE_TERMINAL_ID_STORAGE_KEY)) {
              await addAdditionalDetails({
                variables: {
                  bookingReference: reference,
                  additionalDetails: [
                    {
                      key: `tablet-payment-${moment().format('HH:mm:ss.DDMMYY')}`,
                      value: `${user.paymentLocation && user.paymentLocation.name}-${Number(fields.amount).toFixed(2)}`
                    }
                  ]
                }
              });
            }
          } catch (err) {
            setShowSPTMError(true);
          }
        };

        return (
          <SplitPayment
            title={<FormattedMessage id="payments.AmountToPayByCard" />}
            label={<FormattedMessage id="payments.recordPayment" />}
            loading={loading}
            disabled={hasInvalidInputs}
            onSubmit={handleSubmit}
          >
            <Modal isOpen={showSPTMError} onClose={toggleSPTMErrorModal} title={<FormattedMessage id="cpPayment.paymentFailed" />}>
              <FormattedMessage id="cpPayment.syncMobileError" />
              <br />
              <br />
              <FormattedMessage id="cpPayment.desktopError" />
            </Modal>

            <CurrencyInput
              disabled={isPutOnHoldWithDeposit ? false : isOnHold}
              currency={currency}
              name="amount"
              onChange={handleChange}
              value={fields.amount}
              maxLength={9}
            />
            <Errors>
              {invalidInputs.amount && Number(fields.amount) === 0 && <FormattedMessage id="payments.amountZero" />}
              {invalidInputs.amount && Number(fields.amount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
            </Errors>
          </SplitPayment>
        );
      }}
    </ValidationForm>
  );
};

CustomerPresentCardPayment.propTypes = {
  customer: customerShape,
  reference: PropTypes.string,
  currentAmount: PropTypes.string,
  onAmountChange: PropTypes.func,
  isOnHold: PropTypes.bool,
  currency: PropTypes.string,
  history: historyShape,
  totalPriceToBePaid: PropTypes.number,
  isExchange: PropTypes.bool,
  passengers: passengersShape,
  user: PropTypes.shape({
    isStation: PropTypes.bool,
    paymentLocation: PropTypes.shape({
      name: PropTypes.string
    })
  }),
  isPutOnHoldWithDeposit: PropTypes.bool
};

export default CustomerPresentCardPayment;
